/* eslint-disable indent */
import React, { useEffect, useState } from 'react';
import { Col, Form, Input, InputNumber, Row, Switch, Tag } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { FormInstance } from 'antd/lib/form';
import { ClientFeatureConfig } from '../../types/Feature';
import FormListAddButton from '../FormListAddButton';
import FeatureStatusInput from '../Shared/Inputs/FeatureStatusInput';
import { Fleet, FleetsConfigAttributes } from '../../../../server/src/models';

const cardHeaderTitleStyle = { fontSize: '18px' };

interface FleetsFormSectionProps {
    formInstance: FormInstance;
    fleetsConfig?: ClientFeatureConfig<FleetsConfigAttributes>;
    isDetailMode?: boolean;
    // eslint-disable-next-line react/no-unused-prop-types
    enableControls?: boolean;
    resetFormFlag?: boolean;
}

interface FleetInputProps {
    fieldName: number;
    isDetailMode?: boolean;
    isFeatureDisabled: boolean;
    fleet?: Fleet;
    remove?: (index: number | number[]) => void;
}

const FleetInput: React.FC<FleetInputProps> = ({
    fieldName,
    fleet,
    isDetailMode,
    isFeatureDisabled,
    remove,
}: FleetInputProps) => (
    <>
        <Row gutter={24}>
            <Col lg={{ span: 2, offset: 1 }} md={{ span: 2, offset: 1 }} sm={{ span: 24 }} xs={{ span: 24 }}>
                <Form.Item
                    label="ID"
                    name={[fieldName, 'id']}
                    rules={[
                        {
                            required: !isFeatureDisabled,
                            message: 'ID is required',
                        },
                    ]}
                >
                    {isDetailMode ? <span>{fleet?.id}</span> : <Input title="Fleet's id" />}
                </Form.Item>
            </Col>
            <Col lg={{ span: 9, offset: 1 }} md={{ span: 10, offset: 1 }} sm={{ span: 24 }} xs={{ span: 24 }}>
                <Form.Item
                    label="Name"
                    name={[fieldName, 'name']}
                    rules={[
                        {
                            required: !isFeatureDisabled,
                            message: 'Name is required',
                        },
                    ]}
                >
                    {isDetailMode ? <span>{fleet?.name}</span> : <Input title="Fleet's name" />}
                </Form.Item>
            </Col>
            <Col lg={{ span: 9, offset: 1 }} md={{ span: 9, offset: 1 }} sm={{ span: 24 }} xs={{ span: 24 }}>
                <Form.Item
                    label="Type"
                    name={[fieldName, 'type']}
                    rules={[
                        {
                            required: !isFeatureDisabled,
                            message: 'Type is required',
                        },
                    ]}
                >
                    {isDetailMode ? <span>{fleet?.type}</span> : <Input title="Fleet's type" />}
                </Form.Item>
            </Col>
            {!isDetailMode && (
                <Col md={1}>
                    <span className="ant-form-text text-secondary" style={{ marginTop: '5px', cursor: 'pointer' }}>
                        <Tag
                            color="red"
                            style={{ cursor: 'pointer' }}
                            onClick={(): void => remove?.(fieldName)}
                            id={`fleet_delete_${fieldName}`}
                        >
                            <DeleteOutlined />
                        </Tag>
                    </span>
                </Col>
            )}
        </Row>
        <Row gutter={24}>
            <Col lg={{ span: 7, offset: 1 }} md={{ span: 7, offset: 1 }} sm={{ span: 12 }} xs={{ span: 12 }}>
                <Form.Item label="Max capacity (crates)" name={[fieldName, 'maxCapacity']}>
                    {isDetailMode ? (
                        <span>{fleet?.maxCapacity}</span>
                    ) : (
                        <InputNumber style={{ width: '100%' }} title="Fleet's maximum capacity" />
                    )}
                </Form.Item>
            </Col>
            <Col lg={{ span: 7, offset: 1 }} md={{ span: 7, offset: 1 }} sm={{ span: 12 }} xs={{ span: 12 }}>
                <Form.Item label="Service time at PU" name={[fieldName, 'pickUpServiceTime']}>
                    {isDetailMode ? <span>{fleet?.pickUpServiceTime}</span> : <InputNumber style={{ width: '100%' }} />}
                </Form.Item>
            </Col>
            <Col lg={{ span: 7, offset: 1 }} md={{ span: 7, offset: 1 }} sm={{ span: 12 }} xs={{ span: 12 }}>
                <Form.Item label="Service time at DO" name={[fieldName, 'dropOffServiceTime']}>
                    {isDetailMode ? (
                        <span>{fleet?.dropOffServiceTime}</span>
                    ) : (
                        <InputNumber style={{ width: '100%' }} />
                    )}
                </Form.Item>
            </Col>
        </Row>
    </>
);

const FleetsFormSection: React.FC<FleetsFormSectionProps> = (props: FleetsFormSectionProps) => {
    const { formInstance, fleetsConfig, isDetailMode, resetFormFlag } = props;

    const [isFeatureDisabled, setIsFeatureDisabled] = useState<boolean>(false);

    useEffect(() => {
        if (fleetsConfig) {
            formInstance.setFields([
                { name: 'fleetsEnabled', value: fleetsConfig.config.enabled },
                { name: 'fleets', value: fleetsConfig.config.fleets },
            ]);
            setIsFeatureDisabled(!fleetsConfig.config.enabled);
        }
    }, [fleetsConfig]);

    useEffect(() => {
        setIsFeatureDisabled(true);
    }, [resetFormFlag]);

    return (
        <div className="card border-0" id="fleets-section">
            <div className="card-header border-bottom d-flex justify-content-between align-items-center bg-white">
                <p className="m-0 p-0 h5" style={cardHeaderTitleStyle}>
                    Fleets
                </p>
                <div className="d-inline-flex align-items-center">
                    <span className="mr-2 d-none d-sm-block">Feature is:</span>
                    <FeatureStatusInput
                        name="fleetsEnabled"
                        id="fleets_enabled"
                        isDetailMode={isDetailMode}
                        enabled={fleetsConfig?.config.enabled}
                        isFeatureDisabled={isFeatureDisabled}
                        onChange={(value): void => {
                            setIsFeatureDisabled(!value);
                        }}
                    />
                </div>
            </div>
            <div className="card-body">
                <Row className="h6 text-secondary mb-3">Parameters:</Row>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.List name="fleets">
                            {(fields, { add, remove }): JSX.Element => (
                                <>
                                    {fields.map((field) => (
                                        <FleetInput
                                            key={field.key}
                                            fieldName={field.name}
                                            fleet={fleetsConfig?.config.fleets[field.key]}
                                            isDetailMode={isDetailMode}
                                            isFeatureDisabled={isFeatureDisabled}
                                            remove={remove}
                                        />
                                    ))}
                                    <FormListAddButton
                                        add={add}
                                        text="Add a fleet"
                                        isDetailMode={isDetailMode || false}
                                    />
                                </>
                            )}
                        </Form.List>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default FleetsFormSection;
